import { Button, Tooltip } from '$components'
import { useCreatePetForm } from '.'
import { useMultiStepForm } from '$components'
import { UseFormTrigger, useFormContext } from 'react-hook-form'
import { CreatePetSchema } from '.'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { z } from 'zod'

const getSchemaKeys = (schema: z.ZodSchema): string[] => {
  if (schema instanceof z.ZodObject) {
    return Object.keys(schema.shape)
  }

  if (schema instanceof z.ZodEffects) {
    const innerSchema = schema.innerType()
    if (innerSchema instanceof z.ZodObject) {
      return Object.keys(innerSchema.shape)
    }
  }

  return []
}

export function CreatePetFormControls() {
  const { onCancel, isLoading, onSubmit } = useCreatePetForm()
  const { getCurrentStep, isLastStep, isFirstStep, nextStep, previousStep } = useMultiStepForm()
  const { trigger, getValues } = useFormContext<CreatePetSchema>()
  const { hasSubscriptionExpired } = useTenant()

  const handleSubmit = async () => {
    if (hasSubscriptionExpired) {return}

    const currentStepSchema = getCurrentStep().schema
    const schemaKeys = getSchemaKeys(currentStepSchema)

    const isValid = schemaKeys.length > 0
      ? await trigger(schemaKeys as Parameters<UseFormTrigger<CreatePetSchema>>[0])
      : await trigger()

    if (!isValid) {
      return
    }

    if (isLastStep) {
      onSubmit(getValues())
    } else {
      nextStep()
    }
  }

  const isButtonDisabled = hasSubscriptionExpired || isLoading

  return (
    <div className="flex gap-4 mt-auto ml-auto">
      <Button
        type="button"
        variant="ghost"
        size="lg"
        onClick={onCancel}
      >
        Cancel
      </Button>

      {!isFirstStep && (
        <Button
          type="button"
          variant="ghost"
          size="lg"
          onClick={previousStep}
        >
          Back
        </Button>
      )}

      <Tooltip
        content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
        disabled={!hasSubscriptionExpired}
      >
        <Button
          isLoading={isLoading}
          type="button"
          size="lg"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
        >
          {isLastStep ? 'Create Pet' : 'Next Step'}
        </Button>
      </Tooltip>
    </div>
  )
}
